import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DeviceService } from '../../services/device-service/device.service';
import { CommonService } from '../../services/common.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CustomerService } from '../../services/customer-service/customer.service';
import { MatSelectModule } from '@angular/material/select';
import { finalize } from 'rxjs/operators';
import moment from 'moment';
import { tap } from 'rxjs/operators';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { UtcDateAdapterService } from '../../services/utc-date-adapter.service';
import { resolveApiErrorMessage } from '../../services/api-error-message.util';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import {
  bindFormDirtyTracking,
  clearFormUnsavedChanges,
} from '../../services/unsaved-changes/unsaved-changes-form.util';
import {
  CUSTOMER_DROPDOWN_SELECT_LABEL,
  getGroupDropdownEmptyLabel,
  hasConcreteCustomerSelected,
  isGroupDropdownDisabled,
} from '../../services/group-dropdown.util';
import {
  CURRENT_CUSTOMER_INACTIVE_NOTICE,
  INACTIVE_CUSTOMER_SELECTED_MESSAGE,
  isCustomerActive,
} from '../../services/customer-status.util';

const UPDATE_DEVICE_DIRTY_SOURCE = 'update-device-list';

interface ApiError {
  message: string;
  statusCode: number;
  error: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
}

export const MY_DATE_FORMATS = {
  parse: { dateInput: 'YYYY-MM-DD HH:mm:ss' },
  display: {
    dateInput: 'YYYY-MM-DD HH:mm:ss',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY-MM-DD HH:mm:ss',
    monthYearA11yLabel: 'MMMM YYYY',
  }
};

@Component({
  selector: 'app-update-device-list',
  standalone: true,
  imports: [CommonModule,ReactiveFormsModule,MatButtonModule, MatFormFieldModule,MatDialogModule,
    MatSelectModule,MatDatepickerModule,MatInputModule,MatDatepickerModule,MatNativeDateModule],
    providers: [
      { provide: DateAdapter, useClass: UtcDateAdapterService },
      { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
    ],
  templateUrl: './update-device-list.component.html',
  styleUrl: './update-device-list.component.scss'
})
export class UpdateDeviceListComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);

  deviceForm!: FormGroup;
  customersList: any;
  selectedGatewayId :any;
  groups: any[] = [];
  groupsLoaded = true;
  isSubmitting = false;
  selectedCustomer: any;
  groupName: any;
  maxDate: Date = new Date();
  isBulkEdit = false;
  readonly customerDropdownSelectLabel = CUSTOMER_DROPDOWN_SELECT_LABEL;
  /** Ids of customers returned as inactive by the latest customer fetch (excluded from the dropdown). */
  private inactiveCustomerIds = new Set<string>();
  /** Shown when the device being edited currently belongs to an inactive customer. */
  currentCustomerInactiveNotice = '';

  get groupDropdownEmptyLabel(): string {
    return getGroupDropdownEmptyLabel(
      hasConcreteCustomerSelected(
        this.selectedCustomer ?? this.deviceForm?.getRawValue()?.customerId
      ),
      this.groups?.length ?? 0,
      this.groupsLoaded
    );
  }

  private syncGroupControlEnabledState(): void {
    const disabled = isGroupDropdownDisabled(
      hasConcreteCustomerSelected(
        this.selectedCustomer ?? this.deviceForm?.getRawValue()?.customerId
      ),
      this.groups?.length ?? 0,
      this.groupsLoaded
    );
    const control = this.deviceForm?.get('groupId');
    if (!control) {
      return;
    }
    if (disabled) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }
  constructor(
    private dialogRef: MatDialogRef<UpdateDeviceListComponent>,
    private fb: FormBuilder,
    public commonService: CommonService,
    private snackBar: MatSnackBar,
    private customerService: CustomerService,
    public deviceService: DeviceService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    console.log('Constructor data:', this.data); // Debug log
    this.selectedGatewayId = this.data.rowData.gatewayId;
    this.isBulkEdit = Array.isArray(this.data?.rowData) && this.data.rowData.length > 1;
    // Initialize form
    this.initializeForm();
  }

  private initializeForm() {
    this.deviceForm = this.fb.group({
      customerId: ["", Validators.required],
      groupId: [{ value: '', disabled: true }, Validators.required],
      asset: [""],
      installedOn: [""],
      status:[""],
      carrierProfile: [""]
    });
  }

  ngOnInit(): void {
    console.log('ngOnInit data:', this.data); // Debug log
    bindFormDirtyTracking(
      this.deviceForm,
      UPDATE_DEVICE_DIRTY_SOURCE,
      this.unsavedChanges,
      this.destroyRef
    );

    // Get customers list first and wait for it to complete
    this.getAllCustomer().subscribe(() => {
      // After customers are loaded, handle single device edit
      if (this.data?.rowData?.customer?.id) {
        if (this.isInactiveCustomerId(this.data.rowData.customer.id)) {
          this.setFormValuesForInactiveCurrentCustomer();
        } else {
          this.loadGroupsAndSetFormValues();
        }
      }
    });
  }

  private isInactiveCustomerId(customerId: unknown): boolean {
    return customerId != null && this.inactiveCustomerIds.has(String(customerId));
  }

  /** Keep the device's other values, but require picking an active customer before saving. */
  private setFormValuesForInactiveCurrentCustomer(): void {
    const rowData = this.data.rowData;
    this.currentCustomerInactiveNotice = CURRENT_CUSTOMER_INACTIVE_NOTICE;
    this.selectedCustomer = '';
    this.groups = [];
    this.groupsLoaded = true;
    this.deviceForm.patchValue({
      customerId: '',
      groupId: '',
      asset: rowData.assetName ?? 'N/A',
      installedOn: rowData.installationDate
        ? moment.utc(rowData.installationDate).toDate()
        : null,
      status: rowData.status,
      carrierProfile: rowData.carrierProfileName ?? 'N/A',
    });
    this.syncGroupControlEnabledState();
    clearFormUnsavedChanges(
      this.deviceForm,
      UPDATE_DEVICE_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }

  private handleInactiveCustomerSelected(): void {
    this.selectedCustomer = '';
    this.groups = [];
    this.groupsLoaded = true;
    this.deviceForm.patchValue({ customerId: '', groupId: '' });
    this.syncGroupControlEnabledState();
    this.snackBar.open(INACTIVE_CUSTOMER_SELECTED_MESSAGE, 'Close', {
      duration: 4000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  ngOnDestroy(): void {
    this.unsavedChanges.clear(UPDATE_DEVICE_DIRTY_SOURCE);
  }

  private loadGroupsAndSetFormValues() {
    this.selectedCustomer = this.data.rowData.customer.id;
    this.groupsLoaded = false;
    this.syncGroupControlEnabledState();
    // Get groups for the customer
    this.deviceService.getOtagroupIdList(this.data.rowData.customer.id)
      .subscribe((response: any) => {
        this.groups = response?.data?.content || [];
        this.groupsLoaded = true;
        console.log(this.groups)

        const existingGroupId = this.data.rowData.otaGroup?.id;
        const groupStillAvailable = this.groups.some(
          (g: any) => String(g.id) === String(existingGroupId)
        );

        // Set form values after groups are loaded
        this.syncGroupControlEnabledState();
        this.deviceForm.patchValue({
          customerId: this.data.rowData.customer.id,
          groupId: groupStillAvailable ? existingGroupId : '',
          asset: this.data.rowData.assetName??'N/A',
          installedOn:this.data.rowData.installationDate
          ? moment.utc(this.data.rowData.installationDate).toDate()
          : null,
          status: this.data.rowData.status,
          carrierProfile: this.data.rowData.carrierProfileName??'N/A'

        });
        clearFormUnsavedChanges(
          this.deviceForm,
          UPDATE_DEVICE_DIRTY_SOURCE,
          this.unsavedChanges
        );

        console.log('Form values set:', this.deviceForm.value); // Debug log
      });
  }
  // onDateChange(event: Event): void {
  //   const input = (event.target as HTMLInputElement).value;

  //   // Parse as UTC from input and format with seconds
  //   const utcDateWithSeconds = moment.utc(input).format('YYYY-MM-DD HH:mm:ss');

  //   console.log('Selected UTC datetime:', utcDateWithSeconds);
  //   this.deviceForm.patchValue({ installedOn: utcDateWithSeconds });

  //   // If needed, you can send this formatted UTC value to the backend
  // }
  onDateChange(selectedDate: Date) {
    if (selectedDate) {
      const now = new Date();
      console.log(now)
      console.log(selectedDate.setUTCHours(now.getUTCHours()))
      selectedDate.setDate(selectedDate.getDate() + 1);
      // Keep UTC date with current time
      selectedDate.setUTCHours(now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
      console.log('selected date',selectedDate)
      this.deviceForm.patchValue({ installedOn: selectedDate });
    }
  }

  getAllCustomer() {
    return this.customerService
      .getAllCustomer(this.customerSearchPayload)
      .pipe(
        tap((res) => {
          const customers: any[] = res?.data?.content ?? [];
          this.inactiveCustomerIds = new Set(
            customers
              .filter((customer) => !isCustomerActive(customer))
              .map((customer) => String(customer.id))
          );
          this.customersList = customers.filter((customer) => isCustomerActive(customer));
          console.log('Customers loaded:', this.customersList); // Debug log
        })
      );
  }

  /** Re-fetch customers so a status change made while the dialog was open is caught before saving. */
  onUpdate(): void {
    if (!this.deviceForm.valid) {
      return;
    }
    const customerId = this.deviceForm.getRawValue().customerId;
    this.getAllCustomer().subscribe({
      next: () => {
        if (this.isInactiveCustomerId(customerId)) {
          this.handleInactiveCustomerSelected();
          return;
        }
        this.submitDeviceUpdate();
      },
      error: (error: unknown) => {
        const errorMessage = resolveApiErrorMessage(error, {
          fallback: 'Unable to verify customer status. Please try again.',
        });
        this.snackBar.open(errorMessage, 'Close', {
          duration: 4000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
      }
    });
  }

  private submitDeviceUpdate(): void {
    if (this.deviceForm.valid) {
      let devices = [];

      if (this.data.rowData.length > 1) {
        // Multiple devices: take values from rowData
        console.log('this.data?.rowData?.[0]?.id',this.data?.rowData?.[0]?.id)
        devices = this.data.rowData.map((item:any) => ({
          deviceId: item.id,
          assetName: item.assetName !== 'N/A' ? item.assetName : null,
          status: item.status,
          carrierProfileName: item.carrierProfileName !== 'N/A' ? item.carrierProfileName : null,
          installationDate: item.installationDate
        }));
      }
      else
      {
        // Single device: take values from the form
        devices = [{
          deviceId: this.data?.rowData?.id,
          assetName: this.deviceForm.get('asset')?.value !== 'N/A'
                    ? this.deviceForm.get('asset')?.value
                    : null,
          status: this.deviceForm.get('status')?.value,
          carrierProfileName: this.deviceForm.get('carrierProfile')?.value !== 'N/A'
                    ? this.deviceForm.get('carrierProfile')?.value
                    : null,
          installationDate: this.deviceForm.get('installedOn')?.value
        }];
      }
      const updatedData = {
        // deviceIds: [this.data.rowData.id],
        customerId:+this.deviceForm.getRawValue().customerId,
        groupId:+this.deviceForm.getRawValue().groupId,
        devices
        // devices:[
        //   {
        //     deviceId:this.data?.rowData?.id,
        //     assetName:(this.deviceForm.get('asset')?.value!=='N/A')?(this.deviceForm.get('asset')?.value):null,
        //     status:this.deviceForm.get('status')?.value,
        //     carrierProfileName:(this.deviceForm.get('carrierProfile')?.value!=='N/A')?(this.deviceForm.get('carrierProfile')?.value):null,
        //     installationDate:this.deviceForm.get('installedOn')?.value
        //   }

        // ]
      };
      this.deviceService.updateDeviceList(updatedData)
              .subscribe({
                next: (response: any) => {
                  console.log('Response received:', response);
                  if (!response.status) {
                    console.log('Update failed, message:', response.message);
                    this.snackBar.open(response.message || 'Update failed', 'Close', {
                      duration: 4000,
                      horizontalPosition: 'end',
                      verticalPosition: 'top'
                    });
                    return;
                  }

                  // Handle successful update
                  console.log('Device updated successfully:', response);
                  this.snackBar.open('Device updated successfully', 'Close', {
                    duration: 4000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top'
                  });
                  clearFormUnsavedChanges(
                    this.deviceForm,
                    UPDATE_DEVICE_DIRTY_SOURCE,
                    this.unsavedChanges
                  );
                  // this.commonService.reloadComponent();
                  this.dialogRef.close({ success: true });
                  window.location.reload();
                },
                error: (error: unknown) => {
                  console.error('Error handler called with:', error);
                  const errorMessage = resolveApiErrorMessage(error, {
                    fallback: 'Update failed',
                  });
                  this.snackBar.open(errorMessage, 'Close', {
                    duration: 4000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top'
                  });
                }
              });
}
  }

  customerSearchPayload = {
    page: 0,
    size: 500,
    filters: [
      {
        field: '',
        operator: '',
        value: '',
      },
    ],
    sorts: [
      {
        field: 'customerName',
        direction: 'asc',
      },
    ],
  };

  onCustomerChange(event:any)
  {
    this.selectedCustomer=event.target.value;
    if (this.selectedCustomer) {
      this.currentCustomerInactiveNotice = '';
    }
    this.deviceForm.patchValue({ groupId: '' });
    this.groups = [];
    if(this.selectedCustomer)
    {
      this.groupsLoaded = false;
      this.syncGroupControlEnabledState();
      this.deviceService.getOtagroupIdList(this.selectedCustomer).subscribe((response: any) => {
        this.groups = response.data.content || [];
        this.groupsLoaded = true;
        this.groupName='';
        this.deviceForm.patchValue({ groupId: '' });
        this.syncGroupControlEnabledState();
      });
    } else {
      this.groups = [];
      this.groupsLoaded = true;
      this.syncGroupControlEnabledState();
    }
  }
}
